if (!customElements.get("product-info")) {
  class ProductInfo extends HTMLElement {
    abortController = undefined;
    onVariantChangeUnsubscriber = undefined;
    pendingRequestUrl = null;
    preProcessHtmlCallbacks = [];
    postProcessHtmlCallbacks = [];

    constructor() {
      super();

      this.onWindowLoad = this.onWindowLoad.bind(this);
    }

    get productId() {
      return this.getAttribute('data-product-id');
    }

    get sectionId() {
      return this.dataset.originalSection || this.dataset.sectionId;
    }

    get disableSelectedVariantDefault() {
      return this.dataset.disableSelectedVariantDefault === "true" || false;
    }

    get enableVariantGroupImages() {
      return this.dataset.enableVariantGroupImages === "true" || false;
    }

    get enableHistoryState() {
      return this.dataset.enableHistoryState === "true" || false;
    }

    get viewMode() {
      return this.dataset.viewMode || 'main-product'
    }

    get pickupAvailability() {
      return this.querySelector(`pickup-availability`);
    }

    get productForm() {
      return this.querySelector('product-form'); // fallback: product-form.m-product-form--main
    }

    get productMedia() {
      return this.querySelector(`[id^="MediaGallery-${this.dataset.sectionId}"]`);
    }

    get variantPicker() {
      return this.querySelector('variant-picker');
    }

    get quantityInput() {
      return this.querySelector(`m-quantity-input input`);
    }
    get quantityInputStickyAtc() {
      return this.querySelector(`.m-sticky-addtocart m-quantity-input input`);
    }

    get stickyAtc() {
      return this.querySelector('sticky-atc');
    }

    connectedCallback() {
      if (this.viewMode === "main-product") {
        window.addEventListener("load", this.onWindowLoad);
      }

      this.init();
    }

    disconnectedCallback() {
      window.removeEventListener("load", this.onWindowLoad);
      this.onVariantChangeUnsubscriber();
      this.cartUpdateUnsubscriber?.();
    }

    init() {
      this.currentVariant = this.getSelectedVariant(this);
      this.quantityInputs = [];

      if (this.quantityInput) {
        this.quantityInputs.push(this.quantityInput);
      }
      if (this.quantityInputStickyAtc) {
        this.quantityInputs.push(this.quantityInputStickyAtc);
      }

      if (this.disableSelectedVariantDefault) {
        this.handleDisableSelectedVariantDefault();
      }

      this.onVariantChangeUnsubscriber = MinimogEvents.subscribe(
        MinimogTheme.pubSubEvents.optionValueSelectionChange,
        this.handleOptionValueChange.bind(this)
      );

      this.showFeaturedMedia = this.dataset.showFeaturedMedia === "true" || false;

      this.initialMedias = this.productMedia ? this.productMedia.querySelectorAll('.m-product-media--item') : null;
      this.initialThumbs = this.productMedia ? this.productMedia.querySelectorAll(".m-product-media--slider__thumbnails [data-media-type]") : null;

      if (this.enableVariantGroupImages) {
        this.variantGroupImagesData = this.getVariantGroupImagesData();
        this.showFeaturedMedia = false;
      };

      if (this.currentVariant && this.shouldUseVariantImagesBySKU(this.currentVariant)) {
        this.showFeaturedMedia = false;
      }

      this.initQuantityHandlers();

      if (this.currentVariant) {
        const urlHasVariant = new URLSearchParams(window.location.search).has('variant');
        if (!this.showFeaturedMedia || urlHasVariant) {
          this.updateMedia(this.currentVariant, { animate: false });
        }
      }
    }

    getVariantGroupImagesData() {
      const variantGroupData = this.querySelector('#variantGroup[type="application/json"]');
      if (!variantGroupData) return {};

      try {
        return JSON.parse(variantGroupData.textContent) || {};
      } catch (error) {
        console.error('Error parsing variant group images JSON:', error);
        return {};
      }
    }

    handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
      if (!this.contains(event.target)) return;

      const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
      const shouldSwapProduct = this.dataset.url !== productUrl;
      // this.pendingRequestUrl = productUrl;
      const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

      this.renderProductInfo({
        requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
        targetId: target.id,
        callback: shouldSwapProduct
          ? this.handleSwapProduct(productUrl, shouldFetchFullPage, this.viewMode)
          : this.handleUpdateProductInfo(productUrl, this.viewMode),
      });
    }

    renderProductInfo({ requestUrl, targetId, callback }) {
      this.abortController?.abort();
      this.abortController = new AbortController();

      fetch(requestUrl, { signal: this.abortController.signal })
        .then((response) => response.text())
        .then((responseText) => {
          this.pendingRequestUrl = null;
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          callback(html);
        })
        .then(() => {
          // set focus to last clicked option value
          document.querySelector(`#${targetId}`)?.focus();
        })
        .catch((error) => {
          if (error.name === 'AbortError') {
            console.log('Fetch aborted by user');
          } else {
            console.error(error);
          }
        });
    }

    buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
      const params = [];

      !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

      if (optionValues.length) {
        params.push(`option_values=${optionValues.join(',')}`);
      }

      return `${url}?${params.join('&')}`;
    }

    handleUpdateProductInfo(productUrl, viewMode) {
      return (html) => {
        const quickView = html.querySelector('#MainProduct-quick-view__content');
        if (quickView && viewMode === 'quick-view') {
          html = new DOMParser().parseFromString(quickView.innerHTML, "text/html");
        }

        const variant = this.getSelectedVariant(html);

        this.pickupAvailability?.update(variant);
        this.updateOptionValues(html);
        this.updateURL(productUrl, variant?.id);
        this.updateVariantInputs(variant?.id);

        if (!variant) {
          this.setUnavailable();
          return;
        }

        this.updateMedia(variant);

        const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
          const source = html.getElementById(`${id}-${this.sectionId}`);
          const destination = this.querySelector(`#${id}-${this.dataset.sectionId}`);
          if (source && destination) {
            destination.innerHTML = source.innerHTML;
            destination.classList.toggle('m:hidden', shouldHide(source));
          }
        };

        updateSourceFromDestination('Price');
        updateSourceFromDestination("Inventory");
        updateSourceFromDestination("Sku");
        updateSourceFromDestination("Availability");
        updateSourceFromDestination("Volume");
        updateSourceFromDestination('PricePerItem');
        updateSourceFromDestination("StockReposition");
        this.syncStockRepositionMeta(html);

        this.updateQuantityRules(this.sectionId, this.productId, html);
        this.querySelector(`#QuantityRules-${this.dataset.section}`)?.classList.remove('m:hidden');
        this.querySelector(`#VolumeNote-${this.dataset.section}`)?.classList.remove('m:hidden');

        this.syncAddButtonForVariant(variant, html);

        this.currentVariant = variant;

        MinimogEvents.emit(MinimogTheme.pubSubEvents.variantChange, {
          data: { variant, sectionId: this.sectionId, html },
        });

        document.dispatchEvent(
          new CustomEvent('variant:changed', {
            detail: {
              variant: this.currentVariant,
            },
          })
        );
      };
    }

    handleSwapProduct(productUrl, updateFullPage, viewMode) {
      return (html) => {
        const quickView = html.querySelector('#MainProduct-quick-view__content');
        if (quickView && viewMode === 'quick-view') {
          html = new DOMParser().parseFromString(quickView.innerHTML, "text/html");
        }

        const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
        const productInfo = html.querySelector(selector);
        const variant = this.getSelectedVariant(productInfo);
        productInfo.dataset.disableSelectedVariantDefault = false;

        this.updateURL(productUrl, variant?.id);

        if (updateFullPage) {
          document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
          HTMLUpdateUtility.viewTransition(
            document.querySelector('main'),
            html.querySelector('main'),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );
        } else {
          HTMLUpdateUtility.viewTransition(
            this,
            productInfo,
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );
        }

        this.currentVariant = variant;
      };
    }

    getSelectedVariant(productInfoNode) {
      const selectedVariant = productInfoNode.querySelector('variant-picker [data-selected-variant]')?.innerHTML;
      return !!selectedVariant ? JSON.parse(selectedVariant) : null;
    }

    updateURL(url, variantId) {
      if (this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${url}${this.enableHistoryState && variantId ? `?variant=${variantId}` : ''}`);
    }

    updateVariantInputs(variantId) {
      document
        .querySelectorAll(`#product-form-${this.dataset.sectionId}, #product-form-installment-${this.dataset.sectionId}`)
        .forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
    }

    updateOptionValues(html) {
      const variantPicker = html.querySelector('variant-picker');
      if (variantPicker) {
        const selectedPresentacion = this.variantPicker
          ?.querySelector('presentacion-picker input[type="radio"]:checked')
          ?.value;

        HTMLUpdateUtility.viewTransition(this.variantPicker, variantPicker, this.preProcessHtmlCallbacks);

        if (selectedPresentacion) {
          this.restorePresentacionSelection(selectedPresentacion);
        }
      }
    }

    restorePresentacionSelection(selectedValue) {
      const presentacionPicker = this.variantPicker?.querySelector('presentacion-picker');
      if (!presentacionPicker) return;

      const input = Array.from(
        presentacionPicker.querySelectorAll('input[type="radio"]')
      ).find((radio) => radio.value === selectedValue);

      if (!input) return;

      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: false }));
    }

    setUnavailable() {
      const productForm = document.getElementById(
        `product-form-${this.sectionId}`
      );
      const addButton = productForm.querySelector('[name="add"]');

      if (addButton) {
        this.toggleAddButton(true, window.MinimogStrings.unavailable);
      }

      const selectors = [
        "Price",
        "Inventory",
        "Sku",
        "Availability"
      ]
        .map((id) => `#${id}-${this.sectionId}`)
        .join(", ");
      document
        .querySelectorAll(selectors)
        .forEach((selector) => selector.classList.add('m:hidden'));
    }

    toggleAddButton(disable = true, text, modifyClass = true) {
      const productForm = document.getElementById(`product-form-${this.dataset.sectionId}`);
      if (!productForm) return;
      const addButton = productForm.querySelector('[name="add"]');
      const addButtonText = productForm.querySelector('[name="add"] > span.m-add-to-cart--text');
      if (!addButton) return;

      if (disable) {
        addButton.setAttribute('disabled', 'disabled');
        if (text) addButtonText.textContent = text;
      } else {
        addButton.removeAttribute('disabled');
        addButton.removeAttribute('data-stock-insufficient');
        addButton.classList.remove('m-add-to-cart--blocked');
        addButtonText.innerHTML = text || window.MinimogStrings.addToCart;
      }

      if (!modifyClass) return;
    }

    getLocationStockMap() {
      const stockScript =
        this.querySelector(`#LocationStock-${this.dataset.sectionId}`) ||
        this.querySelector(`#LocationStock-${this.sectionId}`) ||
        document.getElementById(`LocationStock-${this.sectionId}`);

      if (!stockScript) return null;

      try {
        return JSON.parse(stockScript.textContent);
      } catch (error) {
        console.error('Error parsing location stock JSON:', error);
        return null;
      }
    }

    getLocationStockTotal(variantId) {
      const stockMap = this.getLocationStockMap();
      if (!stockMap || variantId == null) return null;

      const total = stockMap[variantId] ?? stockMap[String(variantId)];
      return total == null ? null : Number(total);
    }

    getHandKnittingMinQty() {
      const stockEl =
        this.querySelector(`#StockReposition-${this.dataset.sectionId}`) ||
        this.querySelector(`#StockReposition-${this.sectionId}`);
      const minQty = parseInt(stockEl?.dataset?.minOrderQty || '6', 10);
      return Number.isNaN(minQty) ? 6 : minQty;
    }

    syncStockRepositionMeta(html) {
      const source =
        html.getElementById(`StockReposition-${this.sectionId}`) ||
        html.getElementById(`StockReposition-${this.dataset.sectionId}`);
      const destination =
        this.querySelector(`#StockReposition-${this.dataset.sectionId}`) ||
        this.querySelector(`#StockReposition-${this.sectionId}`);

      if (!source || !destination) return;

      ['data-location-stock', 'data-min-order-qty', 'data-stock-ok'].forEach((attr) => {
        if (source.hasAttribute(attr)) {
          destination.setAttribute(attr, source.getAttribute(attr));
        } else {
          destination.removeAttribute(attr);
        }
      });
    }

    syncAddButtonForVariant(variant, html) {
      const addButtonUpdated = html?.getElementById(`ProductSubmitButton-${this.sectionId}`);
      const shopifyUnavailable = !variant || variant.available === false;
      let shouldDisable = shopifyUnavailable;
      let buttonText = shopifyUnavailable
        ? window.MinimogStrings.soldOut
        : window.MinimogStrings.addToCart;

      const locationStockTotal = this.getLocationStockTotal(variant?.id);
      const minQty = this.getHandKnittingMinQty();
      const hasLocationStockRules = this.getLocationStockMap() !== null;

      if (!shopifyUnavailable && hasLocationStockRules && locationStockTotal !== null) {
        const stockOk = locationStockTotal >= minQty;
        shouldDisable = !stockOk;

        const stockEl =
          this.querySelector(`#StockReposition-${this.dataset.sectionId}`) ||
          this.querySelector(`#StockReposition-${this.sectionId}`);
        if (stockEl) {
          stockEl.dataset.locationStock = String(locationStockTotal);
          stockEl.dataset.stockOk = stockOk ? 'true' : 'false';
        }

        const productForm = document.getElementById(`product-form-${this.dataset.sectionId}`);
        const addButton = productForm?.querySelector('[name="add"]');
        if (addButton) {
          if (stockOk) {
            addButton.removeAttribute('data-stock-insufficient');
          } else {
            addButton.setAttribute('data-stock-insufficient', '');
          }
        }

        this.syncQuantityMaxForStock(locationStockTotal);
      } else if (addButtonUpdated) {
        shouldDisable = addButtonUpdated.hasAttribute('disabled');
        buttonText =
          addButtonUpdated.querySelector('.m-add-to-cart--text')?.textContent?.trim() ||
          buttonText;
      }

      this.toggleAddButton(shouldDisable, buttonText);
    }

    syncQuantityMaxForStock(locationStockTotal) {
      this.quantityInputs.forEach((input) => {
        if (!input.hasAttribute('data-fixed-min')) return;

        const max = Math.max(0, Number(locationStockTotal) || 0);
        input.setAttribute('data-max', String(max));
        input.max = max;

        const quantityInputWrapper = input.closest('m-quantity-input');
        quantityInputWrapper?.setQuantityBoundries?.(this.sectionId, this.productId);
      });
    }

    updateMedia(variant, options = {}) {
      if (!this.productMedia) return;
      const shouldUseGroupedImages =
        (this.enableVariantGroupImages && this.variantGroupImagesData?.enable) ||
        this.shouldUseVariantImagesBySKU(variant);

      if (shouldUseGroupedImages) {
        this.handleVariantGroupImage(variant);

        if (this.productMedia.slider) {
          this.productMedia.slider.updateSlides();
          this.productMedia.navSlider?.updateSlides();

          this.productMedia.slider.slideTo(0);
          this.productMedia.navSlider?.slideTo(0);

          this.productMedia.handleSlideChange();
        }
        if (this.productMedia.enableImageZoom) {
          this.productMedia.handlePhotoswipe();
        }
      } else {
        const setActiveMedia = () => {
          if (typeof this.productMedia.setActiveMedia === 'function') {
            this.productMedia.init();
            this.productMedia.setActiveMedia(variant, options);
            return true; // Indicate success
          }
          return false; // Indicate failure
        };

        if (!setActiveMedia()) {
          this.timer = setInterval(() => {
            if (setActiveMedia()) {
              clearInterval(this.timer);
            }
          }, 100);
        }
      }
    }

    handleDisableSelectedVariantDefault() {
      let urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has("variant")) return;

      this.pickerFields = this.querySelectorAll("variant-picker > .m-product-option [data-picker-field]");

      this.pickerFields && this.pickerFields.forEach((field) => {
        if (field.closest("presentacion-picker")) return;

        let pickerType = field.dataset.pickerField;
        switch (pickerType) {
          case "select":
            let selectBox = field.querySelector("select");
            let option = document.createElement("option");
            option.text = this.dataset.variantOptionNoneText;
            option.setAttribute("disabled", "");
            option.setAttribute("selected", "");

            selectBox.add(option, 0);
            break;
          default:
            let checkedInputs = field.querySelectorAll("input:checked");
            checkedInputs &&
              checkedInputs.forEach(function (input) {
                input.removeAttribute("checked");
              });
            break;
        }

        field.dataset.selectedValue = "";
        if (field.querySelector(".option-label--selected")) field.querySelector(".option-label--selected").textContent = "";
      });

      this.setUnavailable();
      this.currentVariant = null;
    }

    appendSecondPositionMedia(medias, initialItems) {
      const secondMediaId = this.productMedia?.dataset.secondPositionMediaId;
      if (!secondMediaId || !initialItems?.length) return medias;

      const secondMedia = Array.from(initialItems).find(
        (media) => media.dataset.mediaId === secondMediaId
      );
      if (!secondMedia) return medias;

      const alreadyIncluded = medias.some(
        (media) => media.dataset.mediaId === secondMediaId
      );
      if (alreadyIncluded) return medias;

      return [...medias, secondMedia];
    }

    handleVariantGroupImage(variant) {
      const selectedVariantData = (this.variantGroupImagesData?.mapping || []).find(
        (item) => Number(item.id) === variant.id
      );

      const selectedMedias = (initialItems) => {
        if (!selectedVariantData?.media?.length) {
          return this.handleVariantImagesBySKU(variant, initialItems);
        }

        const selectedVariantMedias = Array.from(initialItems)
          .map((media) => {
            const index = selectedVariantData.media.indexOf(media.dataset.mediaId);
            const mediaType = media.dataset.mediaType;
            if (index !== -1 || mediaType !== 'image') {
              return media;
            }
            return null;
          })
          .filter(Boolean);

        if (selectedVariantMedias.length <= 0) {
          return Array.from(initialItems).map((media, index) => {
            media.dataset.index = index;

            media.removeAttribute('style'); // Avoid style of swipe slide item
            media.querySelector('.m-product-media').setAttribute("style", `--animation-order: ${index + 1};`);

            return media;
          });
        }

        const orderedMedias = Object.values(selectedVariantData.media)
          .map(id => selectedVariantMedias.find(media => media.dataset.mediaId === id))
          .filter(Boolean);

        const remainingMedias = selectedVariantMedias.filter(media => !Object.values(selectedVariantData.media).includes(media.dataset.mediaId));

        return Array.from([...orderedMedias, ...remainingMedias]).map((media, index) => {
          media.dataset.index = index;

          media.removeAttribute('style'); // Avoid style of swipe slide item
          media.querySelector('.m-product-media').setAttribute("style", `--animation-order: ${index + 1};`);

          return media;
        });
      }

      const mediaWrapper = this.productMedia.querySelector(".m-media-gallery__list");
      const thumbWrapper = this.productMedia.querySelector(".m-product-media--slider__thumbnails .swiper-wrapper");

      const layout = this.productMedia.dataset.layout;

      mediaWrapper.innerHTML = "";
      thumbWrapper.innerHTML = "";

      const filteredMedias = this.appendSecondPositionMedia(
        selectedMedias(this.initialMedias),
        this.initialMedias
      ).map((media, index) => this.prepareMediaItem(media, index));

      const filteredThumbs = this.appendSecondPositionMedia(
        selectedMedias(this.initialThumbs),
        this.initialThumbs
      ).map((media, index) => this.prepareMediaItem(media, index));

      filteredMedias.forEach((media, index) => {
        if (layout === "layout-2") {
          media.classList.remove("m-col-span-2");
          if (index % 3 === 0) {
            media.classList.add("m-col-span-2");
          }
        }
        mediaWrapper.append(media);
      });
      filteredThumbs.forEach((media) => {
        media.classList.toggle("swiper-slide-thumb-active", media.dataset.index === "0");
        thumbWrapper.append(media);
      });
    }

    shouldUseVariantImagesBySKU(variant) {
      if (!variant?.sku || !this.initialMedias?.length) return false;

      const productData = this.getProductData();
      const skuGroup = this.getVariantSkuGroup(variant, productData);
      if (!skuGroup.length) return false;

      return Array.from(this.initialMedias).some((media) => {
        if (media.dataset.mediaType !== 'image') return false;

        const fileName = this.getMediaFileName(media);
        if (!fileName) return false;

        const fileSKU = fileName.includes('--') ? fileName.split('--')[0] : fileName;
        return skuGroup.includes(fileSKU);
      });
    }

    handleVariantImagesBySKU(variant, initialItems = this.initialMedias) {
      if (!variant || !initialItems?.length) return [];

      const productData = this.getProductData();
      const skuGroup = this.getVariantSkuGroup(variant, productData);
      if (!skuGroup.length) {
        return Array.from(initialItems).map((media, index) => this.prepareMediaItem(media, index));
      }

      const mediaIdToFileName = new Map();
      Array.from(initialItems).forEach((media) => {
        const mediaId = media.dataset.mediaId;
        const fileName = this.getMediaFileName(media);
        if (mediaId && fileName) {
          mediaIdToFileName.set(mediaId, fileName);
        }
      });

      const matchedImages = Array.from(initialItems).filter((media) => {
        if (media.dataset.mediaType !== 'image') return false;

        const mediaId = media.dataset.mediaId;
        const fileName = mediaIdToFileName.get(mediaId);
        if (!fileName) return false;

        const fileSKU = fileName.includes('--') ? fileName.split('--')[0] : fileName;
        return skuGroup.includes(fileSKU);
      });

      const seenMediaIds = new Set();
      const uniqueMatchedImages = matchedImages.filter((media) => {
        const mediaId = media.dataset.mediaId;
        if (!mediaId || seenMediaIds.has(mediaId)) return false;
        seenMediaIds.add(mediaId);
        return true;
      });

      uniqueMatchedImages.sort((a, b) => {
        const fileNameA = mediaIdToFileName.get(a.dataset.mediaId) || '';
        const fileNameB = mediaIdToFileName.get(b.dataset.mediaId) || '';
        return this.getMediaOrderNumber(fileNameA) - this.getMediaOrderNumber(fileNameB);
      });

      const nonImageMedias = Array.from(initialItems).filter((media) => media.dataset.mediaType !== 'image');
      const mediasToRender = uniqueMatchedImages.length
        ? [...uniqueMatchedImages, ...nonImageMedias]
        : Array.from(initialItems);

      return this.appendSecondPositionMedia(mediasToRender, initialItems).map((media, index) =>
        this.prepareMediaItem(media, index)
      );
    }

    getVariantSkuGroup(variant, productData = this.getProductData()) {
      if (!variant) return [];

      const currentColor = variant.option1;
      if (currentColor && productData?.variants?.length) {
        const sameColorSkus = productData.variants
          .filter((item) => item.option1 === currentColor && item.sku)
          .map((item) => String(item.sku).trim())
          .filter(Boolean);

        if (sameColorSkus.length) {
          return sameColorSkus;
        }
      }

      return variant.sku ? [String(variant.sku).trim()] : [];
    }

    getProductData() {
      if (window._themeProducts?.[this.productId]) {
        return window._themeProducts[this.productId];
      }

      const productJson = this.querySelector('[data-product-json]');
      if (productJson) {
        try {
          const parsedProduct = JSON.parse(productJson.textContent);
          return parsedProduct?.product || parsedProduct;
        } catch (error) {
          console.error('Error parsing product JSON:', error);
        }
      }

      if (window.ShopifyAnalytics?.meta?.product) {
        return window.ShopifyAnalytics.meta.product;
      }

      return null;
    }

    getMediaFileName(media) {
      const mediaElement = media.querySelector('.m-product-media');
      const imageSrc = mediaElement?.dataset?.mediaSrc || '';
      const fullFileName = imageSrc.split('/').pop()?.split('?')[0] || '';
      return fullFileName ? fullFileName.split('.').slice(0, -1).join('.') || fullFileName.split('.')[0] : '';
    }

    getMediaOrderNumber(fileName) {
      if (!fileName.includes('--')) return 999999;

      const suffix = fileName.split('--')[1] || '';
      const match = suffix.match(/^(\d+)/);
      return match ? parseInt(match[1], 10) : 999999;
    }

    prepareMediaItem(media, index) {
      media.dataset.index = index;
      media.removeAttribute('style');
      media.querySelector('.m-product-media')?.setAttribute("style", `--animation-order: ${index + 1};`);
      return media;
    }

    initQuantityHandlers() {
      if (this.quantityInputs.length <= 0) return;

      this.setQuantityBoundries();
      if (!this.hasAttribute("data-original-section-id")) {
        this.cartUpdateUnsubscriber = MinimogEvents.subscribe(
          MinimogTheme.pubSubEvents.cartUpdate,
          this.fetchQuantityRules.bind(this)
        );
      }
    }

    setQuantityBoundries() {
      this.quantityInputs.forEach((input) => {
        const quantityInputWrapper = input.closest('m-quantity-input');
        quantityInputWrapper.setQuantityBoundries(this.sectionId, this.productId);
      });
    }

    fetchQuantityRules() {
      const currentVariantId = this.productForm?.variantIdInput?.value;
      if (!currentVariantId) return;

      this.querySelector('.quantity__rules-cart')?.classList.add('loading');

      fetch(
        `${this.getAttribute(
          "data-url"
        )}?variant=${currentVariantId}&section_id=${this.sectionId}`
      )
        .then((response) => response.text())
        .then((responseText) => {
          const parsedHTML = new DOMParser().parseFromString(
            responseText,
            "text/html"
          );
          this.updateQuantityRules(
            this.sectionId,
            this.productId,
            parsedHTML
          );
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          this.querySelector('.quantity__rules-cart')?.classList.remove('loading');
        });
    }

    updateQuantityRules(sectionId, productId, parsedHTML) {
      if (this.quantityInputs.length <= 0) return;

      this.quantityInputs.forEach((input) => {
        const quantityInputWrapper = input.closest('m-quantity-input');
        quantityInputWrapper.updateQuantityRules(sectionId, productId, parsedHTML);
      });

      this.setQuantityBoundries();
    }

    addRecentViewedProduct() {
      const cookies = getCookie('m-recent-viewed-products')
      let products = cookies ? JSON.parse(cookies) : []
      if (products.indexOf(MinimogSettings.productHandle) === -1) {
        products.unshift(MinimogSettings.productHandle)
        products = products.slice(0, 20)
        setCookie('m-recent-viewed-products', JSON.stringify(products));
      }
    }

    onWindowLoad() {
      this.acc = [];
      MinimogTheme.CompareProduct && MinimogTheme.CompareProduct.setCompareButtonsState();
      MinimogTheme.Wishlist && MinimogTheme.Wishlist.setWishlistButtonsState();
      this.addRecentViewedProduct();

      addEventDelegate({
        context: this,
        selector: (window.__minimog_review_selector || '') + '.m-product-collapsible .jdgm-widget-actions-wrapper, .m-product-collapsible .spr-summary-actions-newreview',
        handler: (e) => {
          const index = e.target.closest('.m-product-collapsible').dataset.index
          setTimeout(() => {
            this.acc[Number(index)].setContentHeight()
          }, 300)
        },
        capture: true
      })
    }
  }

  customElements.define("product-info", ProductInfo);
}
